import React, { useState } from "react";
import GoogleDrivePicker from "./GoogleDrivePicker";
import { isSignedInWithGoogle } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";
import "../styles/documents-page.css";

type SelectedFile = {
    id: string;
    name: string;
    mimeType: string;
    sizeBytes: number;
    iconUrl: string;
    url: string;
};

const DocumentsPage: React.FC = () => {
    const { googleConnected } = useAuth();
    const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
    const [error, setError] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");

    const signedInWithGoogle = googleConnected || isSignedInWithGoogle();

    const handleFilesSelected = (files: SelectedFile[]) => {
        setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
        setSuccessMessage(`Successfully added ${files.length} file(s)`);
        setError("");

        // Clear success message after 3 seconds
        setTimeout(() => {
            setSuccessMessage("");
        }, 3000);
    };

    const handleError = (errorMessage: string) => {
        setError(errorMessage);
        setSuccessMessage("");
    };

    const handleViewFile = (url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
    };

    const getFileTypeLabel = (mimeType: string): string => {
        if (mimeType.includes("document")) return "Document";
        if (mimeType.includes("spreadsheet")) return "Spreadsheet";
        if (mimeType.includes("presentation")) return "Presentation";
        if (mimeType.includes("pdf")) return "PDF";
        if (mimeType.includes("image")) return "Image";
        if (mimeType.includes("video")) return "Video";
        if (mimeType.includes("folder")) return "Folder";
        return "File";
    };

    return (
        <div className="documents-page">
            <div className="documents-header">
                <h1>My Documents</h1>
                <p className="documents-subtitle">
                    Select files from your Google Drive to manage them here
                </p>
            </div>

            {!signedInWithGoogle && (
                <div className="documents-warning">
                    <p>Please sign in with Google to access Drive files</p>
                </div>
            )}

            <div className="documents-actions">
                <GoogleDrivePicker
                    onFilesSelected={handleFilesSelected}
                    onError={handleError}
                />
            </div>

            {error && (
                <div className="documents-error">
                    <p>{error}</p>
                </div>
            )}

            {successMessage && (
                <div className="documents-success">
                    <p>{successMessage}</p>
                </div>
            )}

            {selectedFiles.length > 0 && (
                <div className="documents-list">
                    <h2>Selected Files ({selectedFiles.length})</h2>
                    <div className="documents-grid">
                        {selectedFiles.map((file) => (
                            <div key={file.id} className="document-card">
                                <div className="document-icon">
                                    {file.iconUrl ? (
                                        <img src={file.iconUrl} alt={file.name} />
                                    ) : (
                                        <div className="document-icon-placeholder">
                                            {getFileTypeLabel(file.mimeType).charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <div className="document-info">
                                    <h3 className="document-name" title={file.name}>
                                        {file.name}
                                    </h3>
                                    <div className="document-meta">
                                        <span className="document-type">
                                            {getFileTypeLabel(file.mimeType)}
                                        </span>
                                        {file.sizeBytes > 0 && (
                                            <span className="document-size">
                                                {formatFileSize(file.sizeBytes)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    className="document-view-button"
                                    onClick={() => handleViewFile(file.url)}
                                    aria-label={`View ${file.name}`}
                                >
                                    View
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {selectedFiles.length === 0 && signedInWithGoogle && !error && (
                <div className="documents-empty">
                    <p>No files selected yet. Click the button above to select files from Google Drive.</p>
                </div>
            )}
        </div>
    );
};

export default DocumentsPage;

